import { SEO } from "@/components/SEO";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle, XCircle, Phone, MessageSquare, Clock, Shield, ArrowRight, Zap, Layers } from "lucide-react";

const LandscapingQuoteForm = () => {
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const faqs = [
    {
      question: "How many fields should a landscaping quote form have?",
      answer: "4-5 fields maximum: Name, Phone, City/Zip, and Service Type. An optional 'Project Type' dropdown (maintenance, design, hardscape) can help route leads—but keep it optional to avoid friction."
    },
    {
      question: "Should I ask for project details on the form?",
      answer: "Keep it minimal. A large text box labeled 'Describe your project' intimidates people and slows them down. Get the phone number, call them, and gather details in conversation where you can ask follow-up questions."
    },
    {
      question: "How do I handle different service types (maintenance vs. hardscape)?",
      answer: "Add an optional dropdown with categories like 'Maintenance,' 'Design/Install,' 'Hardscape/Patios,' and 'Not Sure.' This helps you prepare for the call without requiring visitors to classify themselves."
    },
    {
      question: "Should landscaping forms be different from lawn care forms?",
      answer: "Slightly. Landscaping projects are often bigger investments, so visitors may expect a more detailed process—but the initial form should still be short. Use the call to discuss scope, timeline, and budget."
    },
    {
      question: "How fast should I respond to landscaping leads?",
      answer: "Within 5-15 minutes is ideal. Landscaping leads often request quotes from multiple companies. The first to call with a professional, helpful response usually wins."
    },
    {
      question: "Do I need separate forms for residential and commercial?",
      answer: "One form works for most landscapers. You can add an optional 'Residential or Commercial?' toggle, but many prefer to determine this on the call to avoid overcomplicating the form."
    },
    {
      question: "Should I show pricing on the form page?",
      answer: "You can mention 'starting at' ranges to set expectations, but avoid detailed pricing that might scare off leads before you've had a chance to explain your value. Save specifics for the consultation."
    },
    {
      question: "What's the best CTA button text for landscaping?",
      answer: "Action-oriented and specific: 'Get Your Free Quote,' 'Request a Consultation,' or 'Start Your Project.' Avoid generic text like 'Submit' or 'Send.'"
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-quote-form/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-quote-form/#webpage",
    "url": "https://www.leafrushmarketing.com/landscaping-quote-form/",
    "name": "Landscaping Quote Form Best Practices | Leaf Rush Marketing",
    "description": "Build a landscaping quote form that converts. Learn the ideal fields, placements, project type options, and follow-up process for hardscape and design leads.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#website"
    },
    "about": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    },
    "dateModified": new Date().toISOString().split('T')[0]
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "sameAs": []
  };

  return (
    <>
      <SEO
        title="Landscaping Quote Form Best Practices | Leaf Rush Marketing"
        description="Build a landscaping quote form that converts. Learn the ideal fields, placements, project type options, and follow-up process for hardscape and design leads."
        canonical="https://www.leafrushmarketing.com/landscaping-quote-form/"
        ogUrl="https://www.leafrushmarketing.com/landscaping-quote-form/"
        ogImage="https://www.leafrushmarketing.com/og-image.jpg"
        keywords="landscaping quote form, landscaping estimate form, landscaping website form, landscaping lead form, hardscape quote form, landscaping contact form"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />

      <div className="pt-24">
        {/* Hero Section */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-primary/5 to-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                Landscaping Quote Form Best Practices
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-4">
                Landscaping projects are big decisions. Your quote form needs to capture leads fast 
                without feeling like a job application.
              </p>
              <p className="text-lg text-muted-foreground">
                Here's how to build a form that converts browsers into consultations—for patios, retaining walls, 
                outdoor living, and everything in between.
              </p>
            </div>
          </div>
        </section>

        {/* Perfect Form Fields */}
        <section className="py-16 bg-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                The Ideal Landscaping Quote Form
              </h2>
              <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
                4 required fields + 1 optional project type dropdown. Enough to route leads properly without creating friction.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">1</div>
                    <h3 className="font-semibold text-lg">Name</h3>
                  </div>
                  <p className="text-muted-foreground">
                    First name works. Landscaping feels more personal than transactional—use their name when you call.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">2</div>
                    <h3 className="font-semibold text-lg">Phone Number</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Your #1 priority. Landscaping customers expect consultations, not email threads. Make this required.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">3</div>
                    <h3 className="font-semibold text-lg">City or Zip Code</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Confirms they're in your service area. For landscapers, travel time matters—this helps you prioritize.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">4</div>
                    <h3 className="font-semibold text-lg">Service Type</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Dropdown: Design, Installation, Maintenance, Hardscape, Other. Helps you prepare for the conversation.
                  </p>
                </div>
              </div>

              {/* Project Type Optional */}
              <div className="mt-8 bg-accent/30 rounded-xl border border-accent p-6">
                <div className="flex items-start gap-4">
                  <Layers className="h-8 w-8 text-primary shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Optional: Project Type Dropdown</h3>
                    <p className="text-muted-foreground mb-4">
                      For landscapers who handle diverse projects, an <strong>optional</strong> dropdown helps route leads:
                    </p>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm">
                      <div className="bg-background/50 px-3 py-2 rounded">🌿 Maintenance / Cleanup</div>
                      <div className="bg-background/50 px-3 py-2 rounded">🎨 Landscape Design</div>
                      <div className="bg-background/50 px-3 py-2 rounded">🧱 Hardscape / Patios</div>
                      <div className="bg-background/50 px-3 py-2 rounded">🏡 Outdoor Living</div>
                      <div className="bg-background/50 px-3 py-2 rounded">🌳 Planting / Softscape</div>
                      <div className="bg-background/50 px-3 py-2 rounded">❓ Not Sure Yet</div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">
                      <strong>Key:</strong> Keep it optional. "Not Sure Yet" is a valid answer. 
                      You don't want to lose leads who can't categorize their project.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Best Placements */}
        <section className="py-16 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Best Form Placements for Landscaping Sites
              </h2>
              <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
                Landscaping visitors often browse your portfolio before converting. Put forms where they'll see them after viewing your work.
              </p>

              <div className="space-y-6">
                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-primary" />
                    Homepage Hero
                  </h3>
                  <p className="text-muted-foreground">
                    Sidebar form or compact inline form. Captures visitors who know what they want immediately.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                    <ArrowRight className="h-5 w-5 text-primary" />
                    Below Project Gallery
                  </h3>
                  <p className="text-muted-foreground">
                    After they've seen your patio work and outdoor kitchens, make it easy to say "I want that."
                    This is a high-intent placement for landscapers.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    Bottom of Service Pages
                  </h3>
                  <p className="text-muted-foreground">
                    After reading about your <Link to="/service-pages-that-rank/" className="text-primary hover:underline">patio or retaining wall services</Link>, 
                    visitors should find a form—not have to navigate elsewhere.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                    <Phone className="h-5 w-5 text-primary" />
                    Mobile Sticky Button
                  </h3>
                  <p className="text-muted-foreground">
                    On mobile, a sticky "Get a Quote" or "Call Now" button at screen bottom. 
                    Mobile visitors browsing galleries want fast access to contact you.
                  </p>
                </div>
              </div>

              <p className="mt-8 text-center text-muted-foreground">
                Need help with <Link to="/service-area-pages-local-seo/" className="text-primary hover:underline">service area pages</Link>? 
                Each one should have a form too.
              </p>
            </div>
          </div>
        </section>

        {/* Spam Control */}
        <section className="py-16 bg-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Spam Control for Landscaping Forms
              </h2>
              <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
                Bots target every form. Here's how to filter them without frustrating real leads.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="h-6 w-6 text-primary" />
                    <h3 className="font-semibold text-lg">Honeypot Field</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Hidden field that bots fill but humans don't see. Any submission with data in this field gets rejected.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Phone className="h-6 w-6 text-primary" />
                    <h3 className="font-semibold text-lg">Phone Validation</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Require valid 10-digit format. Catches bots submitting random strings or fake numbers.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="h-6 w-6 text-primary" />
                    <h3 className="font-semibold text-lg">Time-Based Check</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Forms submitted in under 3 seconds are likely bots. Real people take time to read and fill fields.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <XCircle className="h-6 w-6 text-destructive" />
                    <h3 className="font-semibold text-lg">Skip CAPTCHAs</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Puzzle CAPTCHAs kill conversions. If you're getting spam, tighten the other filters first.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What Happens After Submit */}
        <section className="py-16 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                What Happens After They Submit
              </h2>
              <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
                Landscaping leads often request quotes from 3+ companies. Speed and professionalism determine who wins.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">1</div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Instant Confirmation</h3>
                    <p className="text-muted-foreground">
                      Show a thank-you message: "Thanks! We'll call within a few hours to discuss your project." 
                      Send an auto-text or email so they know the form worked.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">2</div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Call Within 5-15 Minutes</h3>
                    <p className="text-muted-foreground">
                      Landscaping leads expect a more consultative process, but speed still matters. 
                      The first professional response often wins the project.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">3</div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Auto-Text If You're Busy</h3>
                    <p className="text-muted-foreground">
                      "Hi [Name], got your request for a landscape quote! I'm on a job site right now 
                      but I'll call you this afternoon to learn about your project. —[Your Name]"
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">4</div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Schedule the Consultation</h3>
                    <p className="text-muted-foreground">
                      For larger projects, book an on-site consultation during the first call. 
                      This moves them further into your pipeline and away from competitors.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Good vs Bad Examples */}
        <section className="py-16 bg-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Good vs. Bad Form Examples
              </h2>

              <div className="space-y-8">
                {/* Bad Example 1 */}
                <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                  <div className="bg-destructive/10 px-6 py-3 border-b flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-destructive" />
                    <span className="font-semibold text-destructive">Bad Example: Overwhelming Detail Request</span>
                  </div>
                  <div className="p-6">
                    <div className="grid gap-2 mb-4 text-sm">
                      <div className="bg-muted/50 p-2 rounded">Name, Email, Phone, Address</div>
                      <div className="bg-muted/50 p-2 rounded">Property size (sq ft) *</div>
                      <div className="bg-muted/50 p-2 rounded">Budget range * (dropdown with $ amounts)</div>
                      <div className="bg-muted/50 p-2 rounded">Timeline * (When do you need this done?)</div>
                      <div className="bg-muted/50 p-2 rounded">Describe your project in detail * (large textarea)</div>
                      <div className="bg-muted/50 p-2 rounded">Upload photos of your yard *</div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <strong>Problem:</strong> Feels like a job interview. Visitors don't know their sq footage or exact budget. 
                      Photo uploads from mobile are annoying. Most will leave.
                    </p>
                  </div>
                </div>

                {/* Good Example 1 */}
                <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                  <div className="bg-primary/10 px-6 py-3 border-b flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-primary">Good Example: Clean with Optional Context</span>
                  </div>
                  <div className="p-6">
                    <div className="grid gap-3 mb-4 text-sm max-w-sm">
                      <div className="bg-muted/50 p-2 rounded">Name</div>
                      <div className="bg-muted/50 p-2 rounded">Phone *</div>
                      <div className="bg-muted/50 p-2 rounded">Zip Code</div>
                      <div className="bg-muted/50 p-2 rounded">Project Type ▼ (optional)</div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <strong>Why it works:</strong> 4 fields, optional project type helps routing without requiring classification. 
                      Get details on the consultation call where you can ask smart follow-up questions.
                    </p>
                  </div>
                </div>

                {/* Bad Example 2 */}
                <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                  <div className="bg-destructive/10 px-6 py-3 border-b flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-destructive" />
                    <span className="font-semibold text-destructive">Bad Example: Form After Gallery Dead End</span>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-muted-foreground mb-2">
                      Beautiful portfolio page with 20 project photos... ending with no form, no CTA, 
                      just a footer with a phone number in small text.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Problem:</strong> You got them excited about your work and then made it hard to take action. 
                      They'll leave and forget about you.
                    </p>
                  </div>
                </div>

                {/* Good Example 2 */}
                <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                  <div className="bg-primary/10 px-6 py-3 border-b flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-primary">Good Example: Form Follows Portfolio</span>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-muted-foreground mb-2">
                      Gallery page ends with: "Love what you see? Let's discuss your project." 
                      Followed by a simple 4-field form right below.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Why it works:</strong> Capitalizes on the emotional high of seeing great work. 
                      Makes converting as easy as scrolling down.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Checklist */}
        <section className="py-16 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Landscaping Quote Form Checklist
              </h2>

              <div className="bg-card p-8 rounded-xl border shadow-sm">
                <ul className="space-y-4">
                  {[
                    "Form has 4-5 fields max (Name, Phone, Zip, Service, optional Project Type)",
                    "Phone number is required—not optional",
                    "Project Type dropdown is optional with 'Not Sure' option",
                    "Form is visible on homepage above the fold",
                    "Form appears below project gallery/portfolio",
                    "Form appears at bottom of every service page",
                    "Mobile site has sticky call/quote button",
                    "Honeypot field blocks bots (no CAPTCHA)",
                    "Thank-you message sets response time expectations",
                    "Auto-text or email confirms receipt",
                    "You get instant notification on submissions",
                    "Follow-up call happens within 15 minutes"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="mt-8 text-center text-muted-foreground">
                Want a <Link to="/landscaping-website-design/" className="text-primary hover:underline">professionally designed landscaping website</Link> with 
                optimized forms built in? We handle all of this for you.
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16 bg-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Frequently Asked Questions
              </h2>

              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card p-6 rounded-xl border shadow-sm">
                    <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Reading */}
        <section className="py-16 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold mb-6">Related Reading</h2>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/landscaping-website-cost/">
                  <Button variant="outline">Landscaping Website Cost Guide</Button>
                </Link>
                <Link to="/landscaping-website-design/">
                  <Button variant="outline">Landscaping Website Design</Button>
                </Link>
                <Link to="/lawn-care-quote-form/">
                  <Button variant="outline">Lawn Care Quote Form Guide</Button>
                </Link>
                <Link to="/growth-add-ons/gbp/">
                  <Button variant="outline">Google Business Profile</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Ready for a Website That Converts?
              </h2>
              <p className="text-lg opacity-90 mb-8">
                Our <Link to="/websites/" className="underline hover:opacity-80">website subscription</Link> includes 
                optimized quote forms, instant lead notifications, and everything else you need to turn visitors into clients.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#get-started">
                  <Button size="lg" variant="secondary">
                    Get Started
                  </Button>
                </a>
                <Link to="/websites/">
                  <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    See Website Pricing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly */}
        <CalendlyEmbed />

        {/* Resources Link */}
        <section className="py-12 bg-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-muted-foreground">
                Looking for more marketing tips? Check out our <Link to="/resources/" className="text-primary hover:underline">free resources</Link> or 
                <Link to="/contact/" className="text-primary hover:underline"> get in touch</Link> with any questions.
              </p>
              <p className="text-sm text-muted-foreground mt-6">
                Last updated: {today}
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingQuoteForm;